// ================================================================================
// Custom events
//
// To help make it easier to add custom functionality, the theme emits custom
// events (along with their associated data) related to key theme features.
//
// To enable this functionality update the 'useCustomEvents' variable in
// the theme.liquid file to 'true'.
// ================================================================================

/* eslint-disable no-console */

// ================================================================================
// BUNDLE ONE-PER-ORDER RESTRICTION
// Tribal Sport — Eclipse Theme
//
// Fast Bundle bypasses the Eclipse native cart handler entirely
// (confirmed via x-fastbundle-request header + empty line item properties).
// So instead of relying on Eclipse events, we intercept window.fetch directly
// to catch any /cart/add.js or /cart/update.js calls from any app.
//
// NOTE: Multiple apps wrap window.fetch (MWS Fees, Fast Bundle, Eclipse).
// Our wrapper must properly re-throw errors to avoid breaking the fetch chain.
// ================================================================================

const RESTRICTED_BUNDLE_IDS = [
  8221778083887, // Ladies Bundle
  8221790732335, // Managers Bundle
  8187100069935, // Mens Bundle
];

const BUNDLE_NAMES = {
  8221778083887: 'Ladies Bundle',
  8221790732335: 'Managers Bundle',
  8187100069935: 'Mens Bundle',
};

const BUNDLE_HANDLES = [
  'laq-state-team-ladies-bundle',
  'laq-state-team-managers-bundle',
  'laq-state-team-mens-bundle',
];

let lastBundleCounts = {};
let isProcessing = false;

async function fetchCart() {
  const res = await fetch('/cart.js');
  return res.json();
}

function getBundleCounts(cartItems) {
  const counts = {};
  for (const id of RESTRICTED_BUNDLE_IDS) {
    counts[id] = cartItems.filter((item) => item.product_id === id).length;
  }
  return counts;
}

async function removeDuplicateBundle(cartItems, productId) {
  const updates = {};
  let firstSkipped = false;

  cartItems.forEach((item, index) => {
    if (item.product_id !== productId) return;
    if (!firstSkipped) {
      firstSkipped = true; // keep first occurrence
    } else {
      updates[index + 1] = 0; // zero out duplicates (1-based index)
    }
  });

  if (Object.keys(updates).length === 0) return;

  await fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ updates }),
  });
}

function refreshMiniCart() {
  document.dispatchEvent(new CustomEvent('apps:product-added-to-cart'));
}

function showBundleWarning(productId) {
  const name = BUNDLE_NAMES[productId] || 'this bundle';
  alert(
    `Only 1 "${name}" is allowed per order.\n\nPlease complete your checkout before adding another.`
  );
}

async function checkForDuplicateBundles() {
  if (isProcessing) return;

  try {
    const cart = await fetchCart();
    const currentCounts = getBundleCounts(cart.items);

    for (const id of RESTRICTED_BUNDLE_IDS) {
      const current = currentCounts[id] || 0;
      const previous = lastBundleCounts[id] || 0;

      if (current > 1 && current > previous) {
        isProcessing = true;
        console.log(`[TS] Duplicate bundle detected: ${BUNDLE_NAMES[id]}`);
        await removeDuplicateBundle(cart.items, parseInt(id));
        showBundleWarning(parseInt(id));

        // Re-fetch to update counts after removal
        const updatedCart = await fetchCart();
        lastBundleCounts = getBundleCounts(updatedCart.items);

        // Refresh mini cart after duplicate removal
        refreshMiniCart();

        isProcessing = false;
        return; // handle one at a time
      }
    }

    lastBundleCounts = currentCounts;
  } catch (err) {
    console.error('[TS] Bundle check error:', err);
    isProcessing = false;
  }
}

// ================================================================================
// DUPLICATE ATC BUTTON HIDE
//
// On bundle product pages, both Easify (.rbr-bap-new-footer) and TPO/Fast Bundle
// (.tpo_add-to-cart) inject ATC buttons. Easify is the one that actually handles
// the cart add, so we hide the TPO button instead.
// TPO injects dynamically so we need a multi-layered approach to reliably hide it.
// ================================================================================

const currentHandle = window.location.pathname.split('/products/')[1]?.split('?')[0];

if (currentHandle && BUNDLE_HANDLES.includes(currentHandle)) {

  function hideDuplicateATCButton() {
    document.querySelectorAll('.tpo_add-to-cart').forEach((el) => {
      el.style.setProperty('display', 'none', 'important');
    });
  }

  // 1. Try immediately on script load
  hideDuplicateATCButton();

  // 2. Try on DOMContentLoaded
  document.addEventListener('DOMContentLoaded', hideDuplicateATCButton);

  // 3. MutationObserver — catches dynamic injection
  const observer = new MutationObserver(hideDuplicateATCButton);
  observer.observe(document.documentElement, { childList: true, subtree: true });

  // 4. Aggressive polling fallback — every 100ms for 5 seconds
  let attempts = 0;
  const interval = setInterval(() => {
    hideDuplicateATCButton();
    attempts++;
    if (attempts >= 50) clearInterval(interval); // stop after 5s
  }, 100);

  console.log('[TS] Duplicate ATC hide active for:', currentHandle);
}

// ================================================================================
// CORE: Intercept window.fetch
//
// Multiple apps wrap window.fetch (MWS Fees, Fast Bundle, Eclipse main).
// Our wrapper must:
//   1. Always re-throw fetch errors so the chain isn't broken
//   2. Only trigger our logic when the fetch actually succeeded (result.ok)
//   3. Refresh the mini cart after a successful bundle add
// ================================================================================

(function () {
  const _originalFetch = window.fetch;

  window.fetch = async function (...args) {
    let result;

    try {
      result = await _originalFetch.apply(this, args);
    } catch (err) {
      // Re-throw so other apps in the fetch chain aren't broken
      throw err;
    }

    try {
      const url = typeof args[0] === 'string'
        ? args[0]
        : args[0]?.url || '';

      const isCartAdd = url.includes('/cart/add');
      const isCartUpdate = url.includes('/cart/update');

      if ((isCartAdd || isCartUpdate) && result.ok) {
        // Refresh mini cart so items appear without needing a page reload
        setTimeout(refreshMiniCart, 800);

        // Check for duplicate bundles after cart settles
        setTimeout(checkForDuplicateBundles, 600);
      }
    } catch (_) {
      // Never break fetch behaviour due to our logic
    }

    return result;
  };
})();

// Initialize lastBundleCounts on page load so we have a baseline
fetchCart()
  .then((cart) => {
    lastBundleCounts = getBundleCounts(cart.items);
    console.log('[TS] Bundle restriction initialized', lastBundleCounts);
  })
  .catch(() => {});

// ================================================================================
// "Item added to the cart"
//
// - Fires whenever an item has been added to the cart
// - Exposed when the ajax cart is enabled
// - "detail" object data:
//   - The associated product object
// - NOTE: Fast Bundle bypasses this event, but we keep it as a fallback
//   in case other apps or native ATC flows do fire it.
// ================================================================================
document.addEventListener('cart:item-added', function (evt) {
  console.log('Item added to the cart');
  console.log(evt.detail.product);

  // Fallback refresh + duplicate check
  setTimeout(refreshMiniCart, 800);
  setTimeout(checkForDuplicateBundles, 600);
});

// ================================================================================
// "Cart changed"
//
// - Fires whenever the cart is changed
// - Exposed when the ajax cart is enabled
// - "detail" object data:
//   - The cart object
// ================================================================================
document.addEventListener('cart:changed', function (evt) {
  console.log('Cart changed');
  console.log(evt.detail.cart);
});

// ================================================================================
// "Cart error"
//
// - Fires whenever there is an error when adding an item to the cart
//   - Typically due to a product not having sufficient stock
// - "detail" object data:
//   - The error message
// ================================================================================
document.addEventListener('cart:error', function (evt) {
  console.log('Cart error');
  console.log(evt.detail.errorMessage);
});

// ================================================================================
// "Quick cart opened"
//
// - Fires whenever the quick cart is opened
// - Exposed when the ajax cart is enabled
// - "detail" object data:
//   - The cart object
// ================================================================================
document.addEventListener('quick-cart:open', function (evt) {
  console.log('Quick cart opened');
  console.log(evt.detail.cart);
});

// ================================================================================
// "Quick cart closed"
//
// - Fires whenever the quick cart is closed
// - Exposed when the ajax cart is enabled
// ================================================================================
document.addEventListener('quick-cart:close', function () {
  console.log('Quick cart closed');
});

// ================================================================================
// "Product variant changed"
//
// - Fires whenever a variant product is selected
// - Exposed when a 'Variant selectors' block has been added to a product template
//   or featured product section
// - "detail" object data:
//   - The variant object
// ================================================================================
document.addEventListener('product:variant:changing', function (evt) {
  console.log('Product variant changed');
  console.log(evt.detail.variant);
});

// ================================================================================
// "Product quantity updated"
//
// - Fires whenever a product quantity is updated
// - Exposed when a 'Quantity selector' block has been added to a product template
//   or featured product section
// - "detail" object data:
//   - The variant object
//   - The quantity
// ================================================================================
document.addEventListener('product:quantity-update', function (evt) {
  console.log('Product quantity updated');
  console.log(evt.detail.quantity, evt.detail.variant);
});

// ================================================================================
// "Quick view loaded"
//
// - Fires whenever a quick view modal is opened
// - Exposed when the 'Enable quick view' setting is enabled and a quick view
//   modal is opened
// ================================================================================
document.addEventListener('quick-view:loaded', function () {
  console.log('Quick view loaded');
});

/* eslint-enable no-console */
// ================================================================================
// FREE SHIPPING: Firebirds Vendor Auto-Apply
// Tribal Sport — Eclipse Theme
//
// Auto-applies free shipping discount code when:
// 1. Cart contains products from Firebirds vendor
// 2. Cart total >= $150
//
// NOTE: Using vendor detection instead of collections because Shopify's
// product.js API doesn't include collection data for manual collections.
// ================================================================================

// const FREE_SHIPPING_CONFIG = {
//   vendor: 'Firebirds', // Target vendor name
//   minAmount: 150,
//   discountCode: 'FIREBIRDSFREESHIP',
// };

// let freeShippingEligible = false;

// // Check if product is from Firebirds vendor
// async function isProductInCollection(productHandle) {
//   try {
//     const response = await fetch(`/products/${productHandle}.js`);
//     if (!response.ok) return false;
    
//     const product = await response.json();
    
//     // Check if vendor matches
//     const isFirebirds = product.vendor === FREE_SHIPPING_CONFIG.vendor;
    
//     console.log(`[TS] Product "${productHandle}" vendor:`, product.vendor, '| Is Firebirds:', isFirebirds);
    
//     return isFirebirds;
//   } catch (err) {
//     console.error('[TS] Error checking product:', err);
//     return false;
//   }
// }

// // Show/hide free shipping message in cart drawer
// function updateFreeShippingMessage() {
//   // Wait a bit for cart drawer to render
//   setTimeout(() => {
//     // Try multiple selectors for Eclipse theme
//     const cartDrawer = document.querySelector(
//       '.cart-drawer, .quick-cart, [data-cart-drawer], .drawer, [data-section-type="cart-drawer"], aside[id*="cart"]'
//     );
    
//     if (!cartDrawer) {
//       console.log('[TS] Cart drawer not found, trying body fallback');
//       return;
//     }
    
//     console.log('[TS] Cart drawer found:', cartDrawer.className);
    
//     // Remove existing custom message if any
//     const existingMsg = document.querySelectorAll('.ts-freeship-message');
//     existingMsg.forEach(msg => msg.remove());
    
//     // Only show if eligible
//     if (freeShippingEligible) {
//       const message = document.createElement('div');
//       message.className = 'ts-freeship-message';
//       message.style.cssText = `
//         background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
//         color: white;
//         padding: 14px 18px;
//         margin: 16px 16px 8px 16px;
//         border-radius: 8px;
//         text-align: center;
//         font-weight: 600;
//         font-size: 14px;
//         box-shadow: 0 2px 8px rgba(0,0,0,0.1);
//         animation: slideDown 0.3s ease-out;
//       `;
//       message.innerHTML = '🎉 You qualify for FREE SHIPPING on your Firebirds order!';
      
//       // Add animation
//       const style = document.createElement('style');
//       style.textContent = `
//         @keyframes slideDown {
//           from {
//             opacity: 0;
//             transform: translateY(-10px);
//           }
//           to {
//             opacity: 1;
//             transform: translateY(0);
//           }
//         }
//       `;
//       if (!document.querySelector('#ts-freeship-animation')) {
//         style.id = 'ts-freeship-animation';
//         document.head.appendChild(style);
//       }
      
//       // Try multiple content selectors
//       const cartContent = cartDrawer.querySelector(
//         '.cart-drawer__inner, .quick-cart__inner, .cart-drawer__content, .quick-cart__content, .drawer__content, .cart__content, [data-cart-items]'
//       );
      
//       if (cartContent) {
//         cartContent.insertBefore(message, cartContent.firstChild);
//         console.log('[TS] ✅ Free shipping message inserted');
//       } else {
//         // Fallback: insert as first child of drawer
//         cartDrawer.insertBefore(message, cartDrawer.firstChild);
//         console.log('[TS] ✅ Free shipping message inserted (fallback)');
//       }
//     } else {
//       console.log('[TS] Not eligible, no message shown');
//     }
//   }, 300); // Increased timeout
// }

// // Check cart eligibility for free shipping
// async function checkFreeShippingEligibility() {
//   try {
//     const cart = await fetchCart();
//     const cartTotal = cart.total_price / 100;
    
//     console.log('[TS] Checking free shipping eligibility...');
//     console.log('[TS] Cart total: $' + cartTotal.toFixed(2));
    
//     // Check if any item is from Firebirds vendor
//     const vendorChecks = await Promise.all(
//       cart.items.map((item) => isProductInCollection(item.handle))
//     );
    
//     const hasFirebirdsItems = vendorChecks.some((result) => result === true);
    
//     console.log('[TS] Has Firebirds items:', hasFirebirdsItems);
//     console.log('[TS] Meets $150 minimum:', cartTotal >= FREE_SHIPPING_CONFIG.minAmount);
    
//     // Update eligibility
//     freeShippingEligible = hasFirebirdsItems && cartTotal >= FREE_SHIPPING_CONFIG.minAmount;
    
//     // Store in sessionStorage for checkout button
//     if (freeShippingEligible) {
//       sessionStorage.setItem('tsAutoDiscount', FREE_SHIPPING_CONFIG.discountCode);
//       console.log('[TS] ✅ Free shipping eligible! Code:', FREE_SHIPPING_CONFIG.discountCode);
//     } else {
//       sessionStorage.removeItem('tsAutoDiscount');
//       console.log('[TS] ❌ Not eligible for free shipping');
//     }
    
//     // Update cart message
//     updateFreeShippingMessage();
    
//     return freeShippingEligible;
//   } catch (err) {
//     console.error('[TS] Free shipping check error:', err);
//     return false;
//   }
// }

// // Intercept checkout button clicks
// function interceptCheckoutButton(event) {
//   const checkoutButton = event.target.closest(
//     '[name="checkout"], .cart__checkout, button[type="submit"][name="checkout"], a[href="/checkout"]'
//   );
  
//   if (!checkoutButton) return;
  
//   const discountCode = sessionStorage.getItem('tsAutoDiscount');
  
//   if (discountCode) {
//     event.preventDefault();
//     console.log('[TS] Applying discount code:', discountCode);
//     window.location.href = `/checkout?discount=${discountCode}`;
//   }
// }

// // Listen for cart changes
// document.addEventListener('cart:item-added', function () {
//   console.log('[TS] Item added, checking free shipping...');
//   setTimeout(checkFreeShippingEligibility, 800);
// });

// // Also check when cart drawer opens
// document.addEventListener('quick-cart:open', function () {
//   setTimeout(() => {
//     checkFreeShippingEligibility();
//   }, 300);
// });

// // Check when cart is updated (quantity changes, item removed)
// document.addEventListener('cart:changed', function () {
//   console.log('[TS] Cart changed, rechecking eligibility...');
//   setTimeout(checkFreeShippingEligibility, 500);
// });

// // Initialize on page load
// checkFreeShippingEligibility();

// console.log('[TS] Free shipping auto-apply initialized (vendor-based)');

// ================================================================================
// CHECKOUT INTERCEPTION - Multiple Methods
// ================================================================================

// Method 1: Click event listener (capture phase)
// document.addEventListener('click', function(event) {
//   const checkoutButton = event.target.closest(
//     '[name="checkout"], .cart__checkout, .cart__checkout-button, button[type="submit"][name="checkout"], a[href="/checkout"], .button--checkout, [data-checkout-button], .quick-cart__checkout, button[form*="cart"]'
//   );
  
//   if (!checkoutButton) return;
  
//   const discountCode = sessionStorage.getItem('tsAutoDiscount');
  
//   console.log('[TS] 🛒 Checkout button clicked!');
//   console.log('[TS] 💾 Discount in storage:', discountCode);
  
//   if (discountCode) {
//     event.preventDefault();
//     event.stopPropagation();
//     event.stopImmediatePropagation();
//     console.log('[TS] 🚀 Redirecting to checkout with discount:', discountCode);
//     window.location.href = `/checkout?discount=${discountCode}`;
//   }
// }, true);

// // Method 2: Form submission interception
// document.addEventListener('submit', function(e) {
//   const form = e.target;
//   if (form && (form.action.includes('/checkout') || form.action.includes('/cart'))) {
//     const discountCode = sessionStorage.getItem('tsAutoDiscount');
//     if (discountCode) {
//       e.preventDefault();
//       console.log('[TS] 📝 Form submit intercepted, applying discount');
//       window.location.href = `/checkout?discount=${discountCode}`;
//     }
//   }
// }, true);

// // Method 3: Monitor and hijack all checkout links dynamically
// function hijackCheckoutLinks() {
//   document.querySelectorAll('a[href="/checkout"], a[href*="/checkout"], button[name="checkout"]').forEach(element => {
//     if (!element.dataset.tsDiscountHijacked) {
//       element.dataset.tsDiscountHijacked = 'true';
      
//       element.addEventListener('click', function(e) {
//         const discountCode = sessionStorage.getItem('tsAutoDiscount');
//         if (discountCode) {
//           e.preventDefault();
//           e.stopPropagation();
//           console.log('[TS] 🔗 Checkout link hijacked');
//           window.location.href = `/checkout?discount=${discountCode}`;
//         }
//       }, true);
      
//       console.log('[TS] ✅ Hijacked:', element.tagName, element.className || element.name);
//     }
//   });
// }

// hijackCheckoutLinks();
// setInterval(hijackCheckoutLinks, 500);

// document.addEventListener('quick-cart:open', function() {
//   setTimeout(hijackCheckoutLinks, 200);
// });

// console.log('[TS] 🎯 Checkout interception initialized (multi-method)');